import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import calculateDeliveryInformation from 'utils/calculateDeliveryDate'
import getDeliveryInfoColor from '../account/CartItem/PriceInfo/getDeliveryInfoColor'
import Deliverable from 'assets/availability-green.svg'
import DeliverableBulky from 'assets/availability-bulky-green.svg'
import InfoIcon from 'assets/info-icon-black.svg'
import ShopIcon from 'assets/shop.svg'
import Unavailable from 'assets/disabled.svg'
import UnavailableRed from 'assets/disabled-red.svg'
import useTranslations from 'hooks/useTranslations'
import { useSnackbar } from 'modules/Snackbar'

type DeliverableProduct = {
  stock: number
  deliveryDays: number | null
  specialDelivery: boolean
  sellOut: boolean
  unit: { packUnit: string }
  isMeterware: boolean
  isPartnerItem: boolean
  cartItemId?: string
  temporaryDelisted?: boolean
}

type Props = {
  variant: DeliverableProduct
  amount: number
  reduceFontSize?: boolean
  onPartnerItemClick?: () => void
  showPartnerInfo?: boolean
  parentIsCartOrCheckout?: boolean
  hideIcon?: boolean
}

export default function DeliveryInfo(props: Props) {
  const {
    variant,
    amount,
    reduceFontSize,
    onPartnerItemClick,
    showPartnerInfo
  } = props
  const t = useTranslations<'deliveryInfo'>()
  const [stockReached, setStockReached] = React.useState(false)
  const [quantity, setQuantity] = React.useState(0)
  const snackbar = useSnackbar()

  const deliveryInformation = calculateDeliveryInformation(variant, amount)

  const { icon, snippet, variables } = deliveryInformation
  const color = deliveryInformation.isBuyable
    ? getDeliveryInfoColor(icon)
    : theme.colors.shade.b1

  React.useEffect(() => {
    if (!props.parentIsCartOrCheckout) setStockReached(false)
    if (
      snackbar.data.length > 0 &&
      snackbar.data[0].productId === variant.cartItemId &&
      snackbar.data[0].messageKey === 'product_stock_reached'
    ) {
      setStockReached(true)
      setQuantity(snackbar.data[0].quantity || amount)
    }
    if (snippet === 'closeout_exceed') {
      setStockReached(true)
    }
  }, [snackbar.data, snippet])

  return (
    <Wrapper
      className="DeliveryInfo"
      data-cy-ctx="DeliveryInfo"
      $color={color}
      $reduceFontSize={reduceFontSize}
      $isCartCheckout={Boolean(props.parentIsCartOrCheckout) || false}
      $icon={stockReached ? 'notAvailable' : icon}
      $isSpecialWarning={
        snippet === 'full_delivery_at_date_months' ||
        snippet === 'full_delivery_at_date_months_furniture' ||
        snippet === 'full_delivery_at_date_weeks' ||
        snippet === 'full_delivery_at_date_weeks_furniture'
      }
      $hideIcon={props.hideIcon}
    >
      {stockReached ? (
        <div
          data-cy-state="product-stock-reached"
          className="delivery-status-wrapper"
        >
          <span className="icon-wrapper">
            {/* [WDV-3688] Temporär ausgelistete Artikel bekommen geheimes gefärbtes Icon */}
            {!variant.temporaryDelisted && (
              <InfoIcon data-cy-state="product-stock-reached" />
            )}
            {variant.temporaryDelisted && (
              <UnavailableRed data-cy-state="not-deliverable" />
            )}
          </span>
          <span className="info product-stock-reached">
            {props.parentIsCartOrCheckout ? (
              t('closeout_exceed_checkout', {
                stock: quantity || variant.stock
              })
            ) : !variant.stock && variant.temporaryDelisted ? (
              <>
                {t('temporary_delisted')}
                {'.'}{' '}
              </>
            ) : (
              t('closeout_exceed', {
                amount: amount,
                stock: variant.stock
              })
            )}
          </span>
        </div>
      ) : (
        <div className="delivery-status-wrapper">
          {deliveryInformation.isBuyable && (
            <span className="icon-wrapper" data-cy-state="deliverable">
              {variant.temporaryDelisted && variant.stock == 0 ? (
                <UnavailableRed data-cy-state="not-deliverable" />
              ) : variant.specialDelivery ? (
                <DeliverableBulky data-cy-state="bulky-deliverable" />
              ) : (
                <Deliverable data-cy-state="regular-deliverable" />
              )}
            </span>
          )}
          {!deliveryInformation.isBuyable && (
            <span className="icon-wrapper" data-cy-state="not-deliverable">
              {/* [WDV-3688] Temporär ausgelistete Artikel bekommen geheimes gefärbtes Icon */}
              {variant.temporaryDelisted && variant.stock == 0 ? (
                <UnavailableRed data-cy-state="not-deliverable" />
              ) : (
                <Unavailable data-cy-state="not-deliverable" />
              )}
            </span>
          )}
          <div className="info">
            {props.parentIsCartOrCheckout && snippet === 'closeout_exceed' ? (
              <span data-cy-state="show-closeout-exceeded-snippet">
                {t('closeout_exceed_checkout', {
                  stock: quantity || props.variant.stock
                })}
              </span>
            ) : (
              <span>
                {variant.stock === 0 && variant.temporaryDelisted
                  ? t('temporary_delisted')
                  : t(snippet, variables as any)}
                {variant.temporaryDelisted && '.'}
              </span>
            )}
          </div>
        </div>
      )}
      {variant.isPartnerItem && showPartnerInfo && (
        <div className="shop-modal-handle" data-cy-state="is-partner-item">
          <ShopIcon />
          <div
            className="externalPartnerLink"
            onClick={onPartnerItemClick}
            data-cy-handle="external-partner-link"
          >
            {t('shipping_by_partner')}
          </div>
          <InfoIcon
            onClick={onPartnerItemClick}
            data-cy-handle="external-partner-link"
            className="clickableIcon"
          />
        </div>
      )}
    </Wrapper>
  )
}
type WrapperProps = {
  $color: string
  $reduceFontSize?: boolean
  $isCartCheckout?: boolean
  $infoBox?: 'info' | 'warning' | 'error'
  $icon?: 'available' | 'notAvailable' | 'warning'
  $isSpecialWarning?: boolean
  $hideIcon?: boolean
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  > .delivery-status-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    color: ${(p) => p.$color};

    padding: ${theme.spacing('xxs')} 0px;
    ${(p) =>
      p.$reduceFontSize
        ? theme.ty('r-xs', 'bold', 'r-s')
        : theme.ty('r-s', 'bold', 'r-base')}

    > .icon-wrapper {
      ${(p) => p.$hideIcon && 'display: none;'};
      > svg {
        margin-right: ${theme.spacing('xxs')};
        min-width: 1.125rem;
        margin-top: 0.1875rem;

        @media (min-width: ${theme.ms.LG}px) {
          min-width: 1.5rem;
        }
        > path,
        g {
          fill: ${(p) => p.$color};
        }
      }
    }
    ${(p) =>
      p.$icon === 'notAvailable' &&
      p.$isCartCheckout &&
      `
      background-color: ${theme.colors['info-100']};
      color: ${theme.colors.info};
      border: 1px solid ${theme.colors.info};
       > .icon-wrapper 
        > svg 
          > path,
            g > path {
              fill: ${theme.colors.info};            
          }
        
    `}

    ${(p) =>
      p.$isSpecialWarning &&
      p.$isCartCheckout &&
      `
      background-color: ${theme.colors['warning-100']};
      color: ${theme.colors.warning};
      border: 1px solid ${theme.colors.warning};
       > .icon-wrapper 
        > svg 
          > path,
            g > path {
              fill: ${theme.colors.warning};            
          }
        
    `}
    > .info {
      flex: 1;

      /* &.product-stock-reached {
        color: ${theme.colors['info']};
      } */
    }
  }
  > .shop-modal-handle {
    display: flex;
    flex-direction: row;
    align-items: center;

    padding-left: 0px;
    gap: ${theme.spacing('xxs')};
    > svg {
      margin-right: ${theme.spacing('xxs')};
      min-width: 1.125rem;
      margin-top: 0.1875rem;

      @media (min-width: ${theme.ms.LG}px) {
        min-width: 1.5rem;
        margin-right: 0;
      }
    }
    > .externalPartnerLink {
      width: 100%;
      height: 100%;
      text-decoration: underline;
      ${theme.ty('r-s', 'bold', 'r-base')};
      cursor: pointer;
    }

    > .clickableIcon {
      cursor: pointer;
    }
  }
`
